import {clone, findIndex, forEach, ListIterateeCustom, pick} from 'lodash'

/**
 * update the target object by value
 * @param target
 * @param value
 * @param only
 */
export function updateObject<T extends object = any>(
  target: T,
  value: Partial<T>,
  only?: Array<keyof T>,
) {
  let _value = {...value}
  if(only) {
    _value = pick(_value, only)
  }

  forEach(_value, (item, key) => {
    if(item === undefined) {
      return true
    }
    target[key] = clone(item)
  })
}

/**
 * find a item in the list and update it by value
 * @param list
 * @param predicate
 * @param value
 * @param only
 * @param fromIndex
 */
export function findAndUpdate<T extends object = any>(
  list: T[],
  predicate: ListIterateeCustom<T, boolean>,
  value: Partial<T>,
  only?: Array<keyof T>,
  fromIndex?: number,
): T[] {
  const index = findIndex(list, predicate, fromIndex)
  if(index < 0) {
    return list
  }
  const item = list[index]
  updateObject(item, value, only)
  list.splice(index, 1, {...item})
  return list
}

interface UpdateAllOptions<T> {
  /**
   * @default true
   * add left items in valueList or not
   */
  addLeft?: boolean
  /**
   * White list
   */
  only?: Array<keyof T>
}

/**
 * update all items in targetList
 * addLeft is for add items which is not in the targetList but in valueList
 * @param targetList
 * @param valueList
 * @param predicate save as lodash.fine predicate
 * @param options
 */
export function updateAll<T extends object = any>(
  targetList: T[],
  valueList: T[],
  predicate: (value: Partial<T>) => ListIterateeCustom<T, boolean> = (item: any) => ({id: item.id}),
  options: UpdateAllOptions<T> = {},
) {
  const {addLeft = true, only} = options
  const leftList = [...targetList]
  const addList: T[] = []
  forEach(valueList, (value) => {
    const index = findIndex(targetList, predicate(value))
    if(index < 0) {
      addList.push(value)
      return true
    }
    const target = leftList[index]
    let _value = value
    if(only) {
      _value = pick(_value, only)
    }
    targetList.splice(index, 1, {...target, ..._value})
    leftList.splice(index, 1)
  })
  if(addLeft) {
    targetList.push(...addList)
  }
}

/**
 * find a item and remove that
 * @param list
 * @param predicate
 */
export function removeItem<T extends object = any>(
  list: any[],
  predicate: ListIterateeCustom<T, boolean>,
) {
  const index = findIndex(list, predicate)
  if(index < 0) {
    return
  }
  list.splice(index, 1)
  return list
}
